/**
 * PATH: packages/ui/src/qa/QaStatusControl.tsx
 * FILE_ID: FE-QA-STATUS-CONTROL-001
 * PURPOSE: One vocabulary, badge and picker for QA work status, shared by both
 *          QA front doors and used for items and areas alike.
 * WHY: The two doors had drifted into different status UIs over the same
 *      endpoint: the QA hub showed three Pass/Fail/N/A buttons behind a lossy
 *      remap that made in_progress unreachable, while the Jobs Hub had a single
 *      button that cycled blindly through four states. Neither could express
 *      "further action". A shared leaf here is the only way to keep them equal,
 *      since @quotech/ui is the one package both import.
 *      Deliberately API-free, like QaNoteThread: callers own their mutation.
 * FLOW: caller passes the current status -> badge renders label/icon/tone ->
 *       menu opens through the shared ContextMenu primitive -> onChange fires
 *       with the picked status; the caller writes it.
 */

import { useCallback, type MouseEvent } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Circle,
  CircleDot,
  MinusCircle,
  XCircle,
} from 'lucide-react'

import { ContextMenu, useContextMenu, type ContextMenuItem } from '../contextMenu'

/** Wire values, identical to the backend ItemStatus and Area.work_status. */
export type QaStatus =
  | 'not_started'
  | 'in_progress'
  | 'further_action'
  | 'done'
  | 'pass'
  | 'failed'
  | 'not_applicable'

/** Workflow order, used for the menu and any filter row. */
export const QA_STATUS_ORDER: QaStatus[] = [
  'not_started',
  'in_progress',
  'further_action',
  'done',
  'pass',
  'failed',
  'not_applicable',
]

interface StatusMeta {
  label: string
  /** Compact label for tight rows; "F/A" is the site's own shorthand. */
  short: string
  icon: typeof Circle
  /** Semantic design tokens, never raw palette values. */
  icon_class: string
  chip_class: string
}

export const QA_STATUS_META: Record<QaStatus, StatusMeta> = {
  not_started: {
    label: 'Not Started',
    short: 'Not Started',
    icon: Circle,
    icon_class: 'text-muted-foreground',
    chip_class: 'bg-muted text-muted-foreground',
  },
  in_progress: {
    label: 'In Progress',
    short: 'In Progress',
    icon: CircleDot,
    icon_class: 'text-status-info',
    chip_class: 'bg-status-info-muted text-status-info',
  },
  further_action: {
    label: 'Further Action',
    short: 'F/A',
    icon: AlertTriangle,
    icon_class: 'text-status-warn',
    chip_class: 'bg-status-warn-muted text-status-warn',
  },
  done: {
    label: 'Complete',
    short: 'Complete',
    icon: CheckCircle2,
    icon_class: 'text-status-good',
    chip_class: 'bg-status-good-muted text-status-good',
  },
  pass: {
    label: 'Pass',
    short: 'Pass',
    icon: CheckCircle2,
    icon_class: 'text-status-good',
    chip_class: 'bg-status-good-muted text-status-good',
  },
  failed: {
    label: 'Failed',
    short: 'Failed',
    icon: XCircle,
    icon_class: 'text-status-bad',
    chip_class: 'bg-status-bad-muted text-status-bad',
  },
  not_applicable: {
    label: 'Not Applicable',
    short: 'N/A',
    icon: MinusCircle,
    icon_class: 'text-muted-foreground',
    chip_class: 'bg-muted text-muted-foreground',
  },
}

/** Unknown values must render, not crash: older rows may hold retired states. */
export function qaStatusMeta(status: string): StatusMeta {
  return QA_STATUS_META[status as QaStatus] ?? QA_STATUS_META.not_started
}

export function QaStatusBadge({
  status,
  className,
}: {
  status: string
  className?: string
}) {
  const meta = qaStatusMeta(status)
  const Icon = meta.icon
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
        meta.chip_class,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <Icon className="h-3.5 w-3.5" aria-hidden />
      {meta.short}
    </span>
  )
}

/**
 * Status filter row for a checklist.
 *
 * Counts come from the caller because only it knows what it is counting (an
 * area's items, a list's areas). A state with no rows is still shown, greyed:
 * hiding it makes the row jump about as work progresses, and "0 failed" is
 * information a supervisor wants.
 */
export function QaStatusFilterChips({
  counts,
  value,
  onChange,
  className,
  options,
  total: totalProp,
}: {
  counts: Partial<Record<QaStatus, number>>
  /** null means "no filter", rendered as the All chip. */
  value: QaStatus | null
  onChange: (status: QaStatus | null) => void
  className?: string
  options?: ReadonlyArray<{ status: QaStatus; label: string }>
  total?: number
}) {
  const list = options ?? QA_STATUS_ORDER.map((status) => ({ status, label: QA_STATUS_META[status].short }))
  const total = totalProp ?? QA_STATUS_ORDER.reduce((sum, s) => sum + (counts[s] ?? 0), 0)

  const chip = (active: boolean, empty: boolean) =>
    [
      'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
      active
        ? 'border-primary bg-primary text-primary-foreground'
        : 'border-border bg-card text-muted-foreground hover:bg-muted',
      empty && !active ? 'opacity-50' : '',
    ]
      .filter(Boolean)
      .join(' ')

  return (
    // Scrolls rather than wraps: on a phone this must not push the list down.
    <div
      className={['flex items-center gap-2 overflow-x-auto pb-1', className].filter(Boolean).join(' ')}
      role="group"
      aria-label="Filter by status"
    >
      <button
        type="button"
        onClick={() => onChange(null)}
        aria-pressed={value === null}
        className={chip(value === null, false)}
      >
        All <span className="tabular-nums">{total}</span>
      </button>
      {list.map(({ status, label }) => {
        const meta = QA_STATUS_META[status]
        const count = counts[status] ?? 0
        const Icon = meta.icon
        return (
          <button
            key={status}
            type="button"
            // Tapping the active chip clears it, so the row is its own escape.
            onClick={() => onChange(value === status ? null : status)}
            aria-pressed={value === status}
            className={chip(value === status, count === 0)}
          >
            <Icon className="h-3.5 w-3.5" aria-hidden />
            {label} <span className="tabular-nums">{count}</span>
          </button>
        )
      })}
    </div>
  )
}

export interface QaStatusMenuProps {
  status: string
  onChange: (status: QaStatus) => void
  /** Read-only, e.g. a locked area. Renders the badge with no picker. */
  disabled?: boolean
  busy?: boolean
  /** Announced to screen readers, e.g. the item number or area name. */
  targetLabel?: string
  className?: string
}

/**
 * The status badge as a button that opens the shared context menu.
 *
 * Anchored to the button's own rect rather than the pointer position so a touch
 * on a phone opens the menu under the control instead of under the finger.
 */
export function QaStatusMenu({
  status,
  onChange,
  disabled = false,
  busy = false,
  targetLabel,
  className,
}: QaStatusMenuProps) {
  const menu = useContextMenu()

  const open = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const rect = event.currentTarget.getBoundingClientRect()
      menu.openAt({
        clientX: rect.left,
        clientY: rect.bottom,
        currentTarget: event.currentTarget,
        preventDefault: () => event.preventDefault(),
      })
    },
    [menu],
  )

  const items: ContextMenuItem[] = [
    { id: 'header', kind: 'header', label: 'Set status' },
    ...QA_STATUS_ORDER.map((value) => {
      const meta = QA_STATUS_META[value]
      return {
        id: value,
        icon: meta.icon,
        label: meta.label,
        hint: value === status ? 'Current' : undefined,
        onSelect: () => {
          menu.close()
          onChange(value === status ? 'not_started' : value)
        },
      }
    }),
  ]

  if (disabled) return <QaStatusBadge status={status} className={className} />

  return (
    <>
      <button
        type="button"
        onClick={open}
        disabled={busy}
        aria-label={targetLabel ? `Set status for ${targetLabel}` : 'Set status'}
        // 44px min target: this is a one-handed control on a roof.
        className={[
          'inline-flex min-h-11 items-center rounded-lg px-1 transition-opacity hover:opacity-80 disabled:opacity-50',
          className,
        ]
          .filter(Boolean)
          .join(' ')}
      >
        <QaStatusBadge status={status} />
      </button>
      {menu.isOpen && <ContextMenu {...menu.props} items={items} width={200} />}
    </>
  )
}
